import React from "react";
import { motion } from "framer-motion";
import { Headphones, MessagesSquare } from "lucide-react";

const modes = [
  {
    id: "simultaneous",
    icon: Headphones,
    label: "Simultaneous",
    title: "Real-time, as it's spoken.",
    desc: "The interpreter listens and renders the message into the target language at the same time, with only a brief delay. Best for conferences, large meetings, and proceedings where flow can't pause — often delivered with headsets or in a booth.",
    where: "Conferences · Large meetings · Court hearings",
  },
  {
    id: "consecutive",
    icon: MessagesSquare,
    label: "Consecutive",
    title: "Speak, pause, interpret.",
    desc: "The speaker delivers a thought, pauses, and the interpreter relays it accurately into the target language. Ideal for one-on-one or small-group settings where precision and connection matter most.",
    where: "Medical visits · Attorney-client meetings · Depositions · Immigration interviews · Negotiations",
  },
];

export default function Modes() {
  return (
    <section
      id="modes"
      data-testid="modes-section"
      className="bg-[hsl(var(--bg))]"
    >
      <div className="container-edge py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-14">
          <div className="lg:col-span-7">
            <div className="eyebrow" data-testid="modes-eyebrow">How We Interpret</div>
            <h2
              data-testid="modes-heading"
              className="mt-5 font-display text-[hsl(var(--ink))] text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]"
            >
              Two modes,
              <span className="italic text-[hsl(var(--accent))]"> chosen to fit the moment.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pl-8">
            <p className="text-[hsl(var(--text-muted))] text-base leading-relaxed">
              Every assignment is different. We recommend the mode of interpreting
              that best protects clarity, pacing, and confidentiality for the
              setting you're in.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[hsl(var(--line))] border border-[hsl(var(--line))]">
          {modes.map((m, i) => {
            const Icon = m.icon;
            return (
              <motion.article
                key={m.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                data-testid={`mode-card-${m.id}`}
                className="bg-[hsl(var(--surface))] p-8 md:p-12 flex flex-col gap-5"
              >
                <div className="flex items-center justify-between">
                  <span className="eyebrow !text-[hsl(var(--text-muted))]">
                    {m.label} Interpreting
                  </span>
                  <Icon size={22} className="text-[hsl(var(--accent))]" strokeWidth={1.5} />
                </div>

                <h3
                  data-testid={`mode-title-${m.id}`}
                  className="font-display text-2xl md:text-3xl text-[hsl(var(--ink))] leading-tight"
                >
                  {m.title}
                </h3>

                <p className="text-[hsl(var(--text-muted))] leading-relaxed">{m.desc}</p>

                <div className="mt-2 pt-4 border-t border-[hsl(var(--line))]">
                  <div className="eyebrow !text-[hsl(var(--text-muted))]">Often used in</div>
                  <p className="mt-2 text-sm text-[hsl(var(--ink))] leading-relaxed">
                    {m.where}
                  </p>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
